import { engineComponent, EngineComponentFactory, Logger, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { UserControls } from "../Common/UserControls/UserControls";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { GameManager } from "../../Managers/GameManager";
import { GameData } from "../Common/Datas/GameData";
import { GameActions } from "../Common/Actions/GameActions";
import { AppContext } from "../../../Common/AppContext";
import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { LimitDialog } from "../Common/Dialogs/LimitDialog";
import { FreespinsController } from "../../GameComponents/Freespins/FreespinsController";
import { FreespinsActions } from "../Common/Actions/FreespinsActions";
import { ActionsTypes, ActionSet } from "../Common/Actions/ActionSet";
import { TerminalActions } from "../Common/Actions/TerminalActions";
import { FireBirdHelp } from "./FireBirdHelp";
import { FireBirdPaytable } from "./FireBirdPaytable";
import { BetManager } from "../../Managers/BetManager";
import { StyxClientHostEvents } from "./FireBirdTypes";

var GameModules = Communication.Game.GameModules;
var WinType = Communication.Game.WinType;

@engineComponent(ComponentTypes.HtmlElement)
export class FireBirdFreespinsUserControls extends UserControls {
    protected ledStripe: string;

    protected _controller: FreespinsController;
    protected _terminalActions: ActionSet;

    private _userControls: HTMLElement;
    private _userControls_supreme: HTMLElement;
    private _paytable: FireBirdPaytable;
    private _help: FireBirdHelp;
    private _afterWinDelay: FrameTimerEventEmitter;
    private _autoplayDelay: FrameTimerEventEmitter;
    private _retriggerDelay: FrameTimerEventEmitter;
    private _respinDelay: FrameTimerEventEmitter;

    private _featureAutoEndTimer: FrameTimerEventEmitter;
    private _featureAutoEnded: boolean = false;
    private _featureAutoPlayTimer: FrameTimerEventEmitter;

    private _wasGameEnded: boolean = true;

    constructor() {
        super("freespins");
    }

    create(node: Element): void {
        super.create(node);

        this.ledStripe = node.hasAttribute("LedStripe") ? node.getAttribute("LedStripe") : "255;255;255";

        this._terminalActions = new ActionSet(
            `${this._key}.terminalActions`,
            EngineComponentFactory.getComponentName(this),
        );

        this.registerActions();

        this._afterWinDelay = CreateFrameTimer("afterWinDelay");
        this._afterWinDelay.on(FrameTimerEvents.Complete, this.prepareNewGameRound.bind(this));

        this._autoplayDelay = CreateFrameTimer("autoplayDelay");
        this._autoplayDelay.on(FrameTimerEvents.Complete, this.autoplayDelayed.bind(this));

        this._retriggerDelay = CreateFrameTimer("retriggerDelay");
        this._retriggerDelay.on(FrameTimerEvents.Complete, this.retriggerDelayed.bind(this));

        this._respinDelay = CreateFrameTimer("respinDelay");
        this._respinDelay.on(FrameTimerEvents.Complete, this.respin.bind(this));

        this._featureAutoEndTimer = CreateFrameTimer("featureAutoEndTimer");
        this._featureAutoEndTimer.on(FrameTimerEvents.Complete, this.featureAutoEndDelayed.bind(this));

        this._featureAutoPlayTimer = CreateFrameTimer("featureAutoPlayTimer");
        this._featureAutoPlayTimer.on(FrameTimerEvents.Complete, this.featureAutoPlayDelayed.bind(this));

        this._userControls = document.getElementById("freespinsUserControls");

        this.handleClientHostReload = this.handleClientHostReload.bind(this);

        // event 'reload' is fired when client host requests reload of actions (styx/firebird only)
        AppContext.clientHost.on(StyxClientHostEvents.Reload, this.handleClientHostReload);

        let creditBox: HTMLElement = document.getElementById("fsCreditBox");
        let betBox: HTMLElement = document.getElementById("fsBetBox");

        if (AppContext != null && AppContext.gameConfiguration != null) {
            if (creditBox != null) {
                if (AppContext.gameConfiguration.UseCoins) {
                    creditBox.classList.add("denomOn");
                }
                if (AppContext.gameConfiguration.HideAmountsEnabled) {
                    creditBox.classList.add("hideAmountsOn");
                }
            }
            if (betBox != null) {
                if (AppContext.gameConfiguration.HideAmountsEnabled) {
                    betBox.classList.add("hideAmountsOn");
                }
            }
        }
    }

    private handleClientHostReload() {
        if (ModuleManager.gambleActive()) {
            return;
        }

        if (DEBUG) {
            Logger.debugGroup("FireBirdFreespinsUserControls", `Event 'reload' arrived, actions updated...`);
        }

        if (ModuleManager.freespinsEnabled() || ModuleManager.freespinsActive()) {
            AppContext.clientHost.setLEDStripe(this.ledStripe, 0);
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        }
    }

    protected get terminalActions(): ActionSet {
        return this._terminalActions;
    }

    public getAllTerminalActions(): ActionsTypes {
        return this._terminalActions.getAllActions();
    }

    addController(controller: FreespinsController): void {
        this._controller = controller;
        this._controller.on(FreespinsController.StartReceived, this.startReceived.bind(this));
        this._controller.on(FreespinsController.SpinReceived, this.spinReceived.bind(this));
        this._controller.on(FreespinsController.EndReceived, this.endReceived.bind(this));
        this._controller.on(FreespinsController.MachineStopped, this.machineStopped.bind(this));
        this._controller.on(FreespinsController.WinCalculationFinished, this.winCalculationFinished.bind(this));
        this._controller.on(FreespinsController.AllWinsFinished, this.allWinsFinished.bind(this));
        this._controller.on(FreespinsController.IntroFinished, this.introFinished.bind(this));
        this._controller.on(FreespinsController.OutroFinished, this.outroFinished.bind(this));
    }

    registerActions(): void {
        this.actions.registerActions({
            [FreespinsActions.ActionStart]: false,
            [FreespinsActions.ActionBack]: false,
            [FreespinsActions.ActionAutoplay]: false,
            [FreespinsActions.ActionAutoplayStop]: false,
            [FreespinsActions.ActionSpin]: false,
            [FreespinsActions.ActionSpinStop]: false,
        });

        this.terminalActions.registerActions({
            [TerminalActions.Payout]: false,
            [TerminalActions.BackToLobby]: false,
            [TerminalActions.StartWithCollect]: false,
            [TerminalActions.Autoplay]: false,
        });
    }

    initReceived(): void {
        this._userControls_supreme = document.getElementById("userControls_supreme");

        super.initReceived();

        this._controller.initReceived();
        this._controller.disableLogo();

        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }

        this._paytable = new FireBirdPaytable();
        this._paytable.initialize();
        this._paytable.changeBet();

        this._help = new FireBirdHelp();
        this._help.initialize();
    }

    startReceived(): void {
        ModuleManager.outroModule(GameModules.Base); //just turn off base userControls ui
        ModuleManager.outroModule(GameModules.Pick);
    }

    spinReceived(): void {
        DataManager.gameState("BonusGameStarted");
        if (
            DataManager.Autoplay &&
            AppContext.gameConfiguration.AutoplayModeSpeed === "fast" &&
            AppContext.gameConfiguration.GameEnableReelsStop &&
            AppContext.gameConfiguration.MinGameRoundDuration === 0
        ) {
            this.stop();
        } else {
            this.actions.changeActionState(
                FreespinsActions.ActionSpinStop,
                AppContext.gameConfiguration.GameEnableReelsStop,
            );
            this.terminalActions.changeActionState(
                TerminalActions.StartWithCollect,
                AppContext.gameConfiguration.GameEnableReelsStop,
            );
            HtmlController.updateActionElements(this.getAllActions());
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        }
    }

    endReceived(): void {
        ModuleManager.outroModule(GameModules.Freespins);
    }

    dispatchAction(action: string): void {
        if (action === TerminalActions.StartWithCollect) {
            if (this.actions.getActionState(FreespinsActions.ActionStart)) {
                action = FreespinsActions.ActionStart;
            } else if (this.actions.getActionState(FreespinsActions.ActionBack)) {
                action = FreespinsActions.ActionBack;
            } else if (this.actions.getActionState(FreespinsActions.ActionSpin)) {
                action = FreespinsActions.ActionSpin;
            } else if (this.actions.getActionState(FreespinsActions.ActionSpinStop)) {
                action = FreespinsActions.ActionSpinStop;
            }
        }

        if (action === TerminalActions.Autoplay) {
            if (this.actions.getActionState(FreespinsActions.ActionAutoplay)) {
                action = FreespinsActions.ActionAutoplay;
                this._help.reset();
            } else if (this.actions.getActionState(FreespinsActions.ActionAutoplayStop)) {
                action = FreespinsActions.ActionAutoplayStop;
                this._help.reset();
            }
        }

        if (this.actions.getActionState(action)) {
            super.dispatchAction(action);

            switch (action) {
                case FreespinsActions.ActionStart:
                    this.start();
                    break;
                case FreespinsActions.ActionBack:
                    this.back();
                    break;
                case FreespinsActions.ActionSpin:
                    this.spin();
                    this._help.reset();
                    break;
                case FreespinsActions.ActionSpinStop:
                    this.stop();
                    break;
                case FreespinsActions.ActionAutoplay:
                    this.autoplay();
                    break;
                case FreespinsActions.ActionAutoplayStop:
                    this.autoplayStop();
                    break;
            }
        }
    }

    start(): void {
        this.actions.disableAllActions();
        this.terminalActions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        this._controller.start();
    }

    spin(): void {
        this._featureAutoPlayTimer.stop();

        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            (GameManager.freespinsResponse.freeSpins.remainingCount - 1).toString(),
        );

        this.actions.disableAllActions();
        this.terminalActions.disableAllActions();
        if (DataManager.Autoplay) {
            this.actions.changeActionState(FreespinsActions.ActionAutoplayStop, DataManager.autoplayEnabled());
            this.terminalActions.changeActionState(TerminalActions.Autoplay, DataManager.autoplayEnabled());
        } else {
            HtmlController.dispatchUiAction("");
        }
        DataManager.updateDataElement(GameData.DataTotalWin);
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            DataManager.updateDataElement(
                GameData.DataTotalWinDenom,
                parseInt(
                    NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                ).toString(),
            );
            DataManager.updateGameDataFromModule(
                GameData.DataTotalWinDenom,
                parseInt(
                    NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                ).toString(),
            );
        }
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        this._controller.spin();
        this._paytable.reset();
        this._help.reset();
        if (!this._wasGameEnded) {
            DataManager.gameState("BonusGameEnded");
        }
        DataManager.gameState("BonusGameEscrow");
    }

    respin(): void {
        DataManager.gameState("BonusGameEscrow");
        this.actions.disableAllActions();
        if (DataManager.Autoplay) {
            this.actions.changeActionState(FreespinsActions.ActionAutoplayStop, DataManager.autoplayEnabled());
            this.terminalActions.changeActionState(TerminalActions.Autoplay, DataManager.autoplayEnabled());
        }
        HtmlController.dispatchUiAction("");
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        this._controller.respin();
    }

    back(): void {
        this._featureAutoEndTimer.stop();
        this._featureAutoPlayTimer.stop();

        if (!this._featureAutoEnded) {
            ModuleManager.setAutoplayAfterFeature(false);
        }

        this.disable();
        ModuleManager.endFreespins();
    }

    stop(): void {
        this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);
        this.terminalActions.changeActionState(TerminalActions.StartWithCollect, false);
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        HtmlController.dispatchUiAction("");
        this._controller.stop();
        this._help.reset();
    }

    end(): void {
        this._controller.end();
    }

    autoplayDelayed(): void {
        this._autoplayDelay.stop();
        if (DataManager.Autoplay) {
            this.autoplay();
        } else {
            this.prepareNewGameRound();
            this.startFeatureAutoPlay();
        }
    }

    autoplay(): void {
        this._afterWinDelay.stop();

        if (DataManager.AutoplayCount === "∞") {
            DataManager.Autoplay = true;
            DataManager.updateDataElement(GameData.DataAutoplayCount, DataManager.AutoplayCount);
            this.spin();
        } else {
            DataManager.AutoplayCount = (parseInt(DataManager.AutoplayCount) - 1).toString();
            if (parseInt(DataManager.AutoplayCount) >= 0) {
                DataManager.Autoplay = true;
                DataManager.updateDataElement(GameData.DataAutoplayCount, DataManager.AutoplayCount);
                this.spin();
            } else {
                DataManager.Autoplay = false;
                this.prepareNewGameRound();
            }
        }
    }

    autoplayStop(): void {
        ModuleManager.setAutoplayAfterFeature(false);

        DataManager.Autoplay = false;
        this.actions.changeActionState(FreespinsActions.ActionAutoplayStop, false);
        this.terminalActions.changeActionState(TerminalActions.Autoplay, false);
        HtmlController.updateActionElement(FreespinsActions.ActionAutoplayStop, false);
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    machineStopped(): void {
        this._wasGameEnded = false;
        if (GameManager.freespinsResponse.currentWin === 0) {
            DataManager.gameState("BonusGameEnded");
            this._wasGameEnded = true;
        }

        if (GameManager.freespinsResponse.wins != null) {
            this._paytable.showWins(GameManager.freespinsResponse.wins);
        }

        if (this.retrigger()) {
            this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);
            this.terminalActions.changeActionState(TerminalActions.StartWithCollect, false);
            HtmlController.updateActionElement(FreespinsActions.ActionSpinStop, false);
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
            return;
        }

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            this.terminalActions.disableAllActions();
            if (GameManager.freespinsResponse.currentWin === 0) {
                if (GameManager.freespinsResponse.totalWin != null) {
                    if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                        DataManager.updateGameDataFromModule(
                            GameData.DataFreeSpinTotalWinDenom,
                            parseInt(
                                NumberUtils.devide(
                                    GameManager.freespinsResponse.totalWin,
                                    BetManager.CoinSize,
                                ).toString(),
                            ).toString(),
                        );
                    } else {
                        DataManager.updateGameDataFromModule(
                            GameData.DataFreeSpinTotalWin,
                            GameManager.freespinsResponse.totalWin.toString(),
                        );
                    }
                }
                DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);
                DataManager.updateDataElement(GameData.DataFreeSpinTotalWinDenom);
                this.actions.changeActionState(FreespinsActions.ActionBack, true);
                this.terminalActions.changeActionState(TerminalActions.StartWithCollect, true);
                AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());

                this.startFeatureAutoEnd();
            }
            HtmlController.updateActionElements(this.getAllActions());
            return;
        } else if (this.isRespin()) {
            this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);

            HtmlController.updateActionElements(this.getAllActions());

            this._respinDelay.start(50);
            return;
        }

        this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);
        this.terminalActions.changeActionState(TerminalActions.StartWithCollect, false);

        if (GameManager.freespinsResponse.currentWin > 0) {
            this._afterWinDelay.start(30);
        } else {
            this.prepareNewGameRound();
        }

        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());

        if (GameManager.freespinsResponse.currentWin === 0) {
            if (DataManager.Autoplay) {
                this._autoplayDelay.start(15);
            } else {
                this.startFeatureAutoPlay();
            }
        }
    }

    winCalculationFinished(): void {
        this._paytable.bounceLogo();

        if (this.retrigger()) {
            return;
        }

        this._afterWinDelay.stop();

        DataManager.updateDataElement(GameData.DataTotalWin);
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            DataManager.updateDataElement(
                GameData.DataTotalWinDenom,
                parseInt(
                    NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                ).toString(),
            );
            DataManager.updateGameDataFromModule(
                GameData.DataTotalWinDenom,
                parseInt(
                    NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                ).toString(),
            );

            if (GameManager.freespinsResponse.totalWin != null) {
                DataManager.updateGameDataFromModule(
                    GameData.DataFreeSpinTotalWinDenom,
                    parseInt(
                        NumberUtils.devide(GameManager.freespinsResponse.totalWin, BetManager.CoinSize).toString(),
                    ).toString(),
                );
            }
        }

        if (GameManager.freespinsResponse.totalWin != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinTotalWin,
                GameManager.freespinsResponse.totalWin.toString(),
            );
        }
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWinDenom);

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            HtmlController.updateActionElements(this.getAllActions());
            return;
        }

        this.prepareNewGameRound();
    }

    allWinsFinished(): void {
        DataManager.gameState("BonusGameEnded");
        this._wasGameEnded = true;

        if (this.retrigger(true)) {
            this._retriggerDelay.start(60);
            return;
        }

        this._afterWinDelay.stop();

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            this.actions.changeActionState(FreespinsActions.ActionBack, true);
            this.terminalActions.changeActionState(TerminalActions.StartWithCollect, true);

            this.startFeatureAutoEnd();

            HtmlController.updateActionElements(this.getAllActions());
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
            return;
        }

        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            this.autoplay();
        } else {
            this.startFeatureAutoPlay();
        }
    }

    retriggerDelayed(): void {
        this._afterWinDelay.stop();

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            this.actions.changeActionState(FreespinsActions.ActionBack, true);
            this.terminalActions.changeActionState(TerminalActions.StartWithCollect, true);
            HtmlController.updateActionElements(this.getAllActions());
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
            return;
        }

        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            this.autoplay();
        }
    }

    initIntro(): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
        );

        DataManager.updateDataElement(GameData.DataTotalWin, GameManager.gameResponse.totalWin.toString());
        DataManager.updateGameDataFromModule(GameData.DataTotalWin, GameManager.gameResponse.totalWin.toString());
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            DataManager.updateDataElement(
                GameData.DataTotalWinDenom,
                parseInt(
                    NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                ).toString(),
            );
            DataManager.updateGameDataFromModule(
                GameData.DataTotalWinDenom,
                parseInt(
                    NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                ).toString(),
            );

            if (GameManager.freespinsResponse.totalWin != null) {
                DataManager.updateGameDataFromModule(
                    GameData.DataFreeSpinTotalWinDenom,
                    parseInt(
                        NumberUtils.devide(GameManager.freespinsResponse.totalWin, BetManager.CoinSize).toString(),
                    ).toString(),
                );
            }
        }

        if (GameManager.freespinsResponse.totalWin != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinTotalWin,
                GameManager.freespinsResponse.totalWin.toString(),
            );
        }
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWinDenom);

        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }
        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }

        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }
        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }

        this.retrigger(true);
        if (GameManager.freespinsResponse.wins != null) {
            this._paytable.showWins(GameManager.freespinsResponse.wins);
        }

        super.initIntro();
        this._controller.initIntro();

        if (this.isLastFreespin()) {
            this.actions.disableAllActions();
            this.actions.changeActionState(FreespinsActions.ActionBack, true);

            this.startFeatureAutoEnd();

            HtmlController.updateActionElements(this.getAllActions());
            this.terminalActions.changeActionState(TerminalActions.StartWithCollect, true);
            AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
            return;
        } else if (this.isRespin()) {
            this._respinDelay.start(0);
            return;
        }

        this.prepareNewGameRound();

        this.startFeatureAutoPlay();
    }

    intro(long: boolean = false): void {
        DataManager.gameState("AutoPlayEnded");

        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
        );

        this._controller.intro(long);

        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }
    }

    introFinished(): void {
        this.prepareNewGameRound();
        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }

        this.startFeatureAutoPlay();
    }

    outro(): void {
        DataManager.Autoplay = false;
        this._featureAutoEnded = false;

        this._controller.outro();
        this._paytable.reset();
        this._help.reset();

        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }

        if (
            this._userControls_supreme != null &&
            !this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.add(HtmlController.DisabledClass);
        }
    }

    outroFinished(): void {
        ModuleManager.introModule(GameModules.Base, true);
        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
    }

    prepareNewGameRound(): void {
        this._afterWinDelay.stop();

        if (GameManager.freespinsResponse.totalWin != null) {
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                DataManager.updateGameDataFromModule(
                    GameData.DataFreeSpinTotalWinDenom,
                    parseInt(
                        NumberUtils.devide(GameManager.freespinsResponse.totalWin, BetManager.CoinSize).toString(),
                    ).toString(),
                );
            } else {
                DataManager.updateGameDataFromModule(
                    GameData.DataFreeSpinTotalWin,
                    GameManager.freespinsResponse.totalWin.toString(),
                );
            }
        }

        DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWinDenom);

        if (DataManager.GameLimit) {
            DataManager.Autoplay = false;
            this.limit();
        } else {
            if (!DataManager.Autoplay) {
                this.actions.changeActionStates({
                    [FreespinsActions.ActionStart]: false,
                    [FreespinsActions.ActionBack]: false,
                    [FreespinsActions.ActionAutoplay]: !DataManager.Autoplay && DataManager.autoplayEnabled(),
                    [FreespinsActions.ActionAutoplayStop]: DataManager.Autoplay && DataManager.autoplayEnabled(),
                    [FreespinsActions.ActionSpin]: true,
                    [FreespinsActions.ActionSpinStop]: false,
                });

                this.terminalActions.changeActionStates({
                    [TerminalActions.StartWithCollect]: true,
                    [TerminalActions.Autoplay]: !DataManager.Autoplay && DataManager.autoplayEnabled(),
                });
            }
        }

        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    limit(): void {
        LimitDialog.openDialog();

        this.actions.changeActionStates({
            [FreespinsActions.ActionStart]: false,
            [FreespinsActions.ActionBack]: false,
            [FreespinsActions.ActionAutoplay]: false,
            [FreespinsActions.ActionAutoplayStop]: false,
            [FreespinsActions.ActionSpin]: false,
            [FreespinsActions.ActionSpinStop]: false,
        });

        this.terminalActions.changeActionStates({
            [TerminalActions.StartWithCollect]: false,
            [TerminalActions.Autoplay]: false,
        });
    }

    enable(): void {
        DataManager.gameState("BonusGameTriggered");
        DataManager.updateGameDataFromModule(
            GameData.DataFreeSpinsRemainingCount,
            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
        );
        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
        );
        DataManager.updateGameDataFromModule(
            GameData.DataFreeSpinsCount,
            GameManager.freespinsResponse.freeSpins.totalCount.toString(),
        );
        DataManager.updateDataElement(
            GameData.DataFreeSpinsCount,
            GameManager.freespinsResponse.freeSpins.totalCount.toString(),
        );
        this.actions.changeActionState(FreespinsActions.ActionStart, true);
        this.terminalActions.changeActionState(TerminalActions.StartWithCollect, true);
        HtmlController.updateActionElement(FreespinsActions.ActionStart, true);
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
        this._controller.enable();
    }

    disable(): void {
        this.actions.disableAllActions();
        this.terminalActions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    private retrigger(updateCount: boolean = false): boolean {
        let wasRetrigger: boolean = false;

        if (GameManager.freespinsResponse.wins != null && GameManager.freespinsResponse.wins.length > 0) {
            for (let i: number = 0; i < GameManager.freespinsResponse.wins.length; i++) {
                if (GameManager.freespinsResponse.wins[i].type === WinType.freeSpins) {
                    if (updateCount) {
                        DataManager.updateDataElement(
                            GameData.DataFreeSpinsRemainingCount,
                            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
                        );
                    }
                    let count: number = GameManager.freespinsResponse.wins[i].customData["count"];
                    if (count != null) {
                        DataManager.updateGameDataFromModule(GameData.DataFreeSpinsCount, count.toString());
                        DataManager.updateGameDataFromModule(GameData.DataFreeSpinsWonCount, count.toString());
                        DataManager.updateDataElement(GameData.DataFreeSpinsCount, count.toString());
                        DataManager.updateDataElement(GameData.DataFreeSpinsWonCount, count.toString());
                    }
                    wasRetrigger = true;
                }
            }
        }

        return wasRetrigger && !this.isLastFreespin();
    }

    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            this.limit();
            return true;
        }

        if (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0 &&
            !this.isRespin()
        ) {
            return true;
        }

        return false;
    }

    private isRespin(): boolean {
        return (
            GameManager.freespinsResponse != null &&
            GameManager.freespinsResponse.respin != null &&
            GameManager.freespinsResponse.respin !== 0
        );
    }

    cycleHelp(): void {
        this._help.cycle();
    }

    /**
     * Dispatch action Back by timer.
     */
    private featureAutoEndDelayed(): void {
        this._featureAutoEndTimer.stop();
        this._featureAutoEnded = true;
        this.dispatchAction(FreespinsActions.ActionBack);

        if (DEBUG) {
            this.log("INFO featureAutoEndDelayed ActionBack");
        }
    }

    /**
     * Dispatch action Autoplay by timer.
     */
    private featureAutoPlayDelayed(): void {
        this._featureAutoPlayTimer.stop();
        this.dispatchAction(FreespinsActions.ActionAutoplay);

        if (DEBUG) {
            this.log("INFO featureAutoPlayDelayed ActionAutoplay");
        }
    }

    /**
     * Auto end feature.
     * Duration is set to preDuration + duration from configuration.
     * @param {number} preDuration
     */
    protected startFeatureAutoEnd(preDuration: number = 0): void {
        if (AppContext.gameConfiguration.FeatureAutoPlay != null && AppContext.gameConfiguration.FeatureAutoPlay > 0) {
            this._featureAutoEndTimer.start(preDuration + AppContext.gameConfiguration.FeatureAutoPlay);
        }
    }

    /**
     * Auto start autoplay in feature.
     * Duration is set from configuration.
     */
    protected startFeatureAutoPlay(): void {
        if (AppContext.gameConfiguration.FeatureAutoPlay != null && AppContext.gameConfiguration.FeatureAutoPlay > 0) {
            this._featureAutoPlayTimer.start(AppContext.gameConfiguration.FeatureAutoPlay);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/FireBird/FireBirdFreespinsUserControls.ts
 **/